/**
 * Scratch & win state for the signed-in dealer.
 *
 * Two live listeners, both cheap and both rules-scoped to the dealer:
 *  - the admin's switch + prize table (`app_config/scratchCard`), so the entry
 *    banner appears/disappears the moment the admin flips it;
 *  - the dealer's own card for the CURRENT round (deterministic doc id, so no
 *    query), which is null until they open the card screen and the worker
 *    draws it.
 * Both are keyed on the signed-in uid: a listener started while signed out is
 * denied by the rules and terminated, so it must be (re)created per user.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ScratchCard, ScratchCardConfig } from '@mitang/shared';
import { useAuth } from '../features/auth/AuthContext';
import { listenMyScratchCard, listenMyScratchCards, listenScratchConfig } from '../lib/repo';

export interface ScratchConfigState {
  /** null = no config / feature off / not readable. */
  config: ScratchCardConfig | null;
  loading: boolean;
}

export function useScratchConfig(): ScratchConfigState {
  const { fbUser } = useAuth();
  const uid = fbUser?.uid ?? null;
  const [state, setState] = useState<ScratchConfigState>({ config: null, loading: true });

  useEffect(() => {
    if (!uid) {
      setState({ config: null, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    return listenScratchConfig(
      (config) => setState({ config, loading: false }),
      () => setState({ config: null, loading: false }),
    );
  }, [uid]);

  return state;
}

export interface MyScratchCardState {
  /** null = not drawn yet for this round (or feature off). */
  card: ScratchCard | null;
  loading: boolean;
  /**
   * Restart the listener. Needed after the draw: a `get` on a card that does
   * not exist yet can be denied by the rules (there is no `resource.data.uid`
   * to match), and a denied listener is terminated for good — so the card the
   * worker just wrote would never arrive without a fresh listener.
   */
  reload: () => void;
}

/** The dealer's own card for the round in `config` (null until drawn). */
export function useMyScratchCard(config: ScratchCardConfig | null): MyScratchCardState {
  const { fbUser } = useAuth();
  const uid = fbUser?.uid ?? null;
  const round = config?.enabled ? config.round : null;
  const [state, setState] = useState<Omit<MyScratchCardState, 'reload'>>({ card: null, loading: true });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!uid || !round) {
      setState({ card: null, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    return listenMyScratchCard(
      round,
      uid,
      (card) => setState({ card, loading: false }),
      () => setState({ card: null, loading: false }),
    );
  }, [uid, round, tick]);

  return { ...state, reload };
}

/** Every card the dealer has drawn, newest first — for the rewards history. */
export function useMyScratchCards(): ScratchCard[] {
  const { fbUser } = useAuth();
  const uid = fbUser?.uid ?? null;
  const [cards, setCards] = useState<ScratchCard[]>([]);

  useEffect(() => {
    if (!uid) {
      setCards([]);
      return;
    }
    return listenMyScratchCards(uid, setCards, () => setCards([]));
  }, [uid]);

  return cards;
}
